import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { TRACKER_API_BASE } from '../BaseUrl/BaseUrl';
import { ADMIN_ROLE_COMPANY, ADMIN_ROLE_SUPER, APP_MODULE_TO_TRACKER_NAME } from '../constants/moduleAccess';
import { ADMIN_PROFILE_UPDATED } from '../utils/adminProfileEvents';

const ModuleContext = createContext(null);

function readAdminProfile() {
  try {
    const raw = window.localStorage.getItem('admin');
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/* eslint-disable react/prop-types */
export function ModuleProvider({ children }) {
  const [accessByTrackerName, setAccessByTrackerName] = useState({});
  const [adminSnapshot, setAdminSnapshot] = useState(() => readAdminProfile());
  const [loading, setLoading] = useState(() => readAdminProfile()?.role === ADMIN_ROLE_COMPANY);

  const fetchModuleAccess = useCallback(async () => {
    const profile = readAdminProfile();
    const token = window.localStorage.getItem('auth-token');
    const companyId = profile?.company_id;

    if (!token || profile?.role !== ADMIN_ROLE_COMPANY || !companyId) {
      setAccessByTrackerName({});
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${TRACKER_API_BASE}/module-access/company-modules`, {
        params: { company_id: companyId },
        headers: { 'x-auth-token': token },
      });
      const modules = res.data?.data?.modules ?? [];
      const next = {};
      modules.forEach(({ module_name: moduleName, access }) => {
        if (moduleName != null) next[moduleName] = Boolean(access);
      });
      setAccessByTrackerName(next);
    } catch (err) {
      console.error(err);
      setAccessByTrackerName({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onProfileChange = () => {
      setAdminSnapshot(readAdminProfile());
      fetchModuleAccess();
    };
    window.addEventListener(ADMIN_PROFILE_UPDATED, onProfileChange);
    setAdminSnapshot(readAdminProfile());
    fetchModuleAccess();
    return () => window.removeEventListener(ADMIN_PROFILE_UPDATED, onProfileChange);
  }, [fetchModuleAccess]);

  const canAccessAppModule = useCallback(
    (modulePath) => {
      const profile = readAdminProfile();
      if (profile?.role === ADMIN_ROLE_SUPER) return true;
      if (profile?.role !== ADMIN_ROLE_COMPANY) return true;

      const trackerName = APP_MODULE_TO_TRACKER_NAME[modulePath];
      if (!trackerName) return true;
      if (!(trackerName in accessByTrackerName)) return true;
      return accessByTrackerName[trackerName] === true;
    },
    [accessByTrackerName]
  );

  const value = {
    loading,
    isSuperAdmin: adminSnapshot?.role === ADMIN_ROLE_SUPER,
    isCompanyAdmin: adminSnapshot?.role === ADMIN_ROLE_COMPANY,
    canAccessAppModule,
    refreshModuleAccess: fetchModuleAccess,
  };

  return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>;
}

export function useModules() {
  const ctx = useContext(ModuleContext);
  if (!ctx) {
    throw new Error('useModules must be used within ModuleProvider');
  }
  return ctx;
}
